import { useState, useMemo } from 'react'
import { UserCog, Plus, Edit, Trash2, Key, Ban, CheckCircle, Search } from 'lucide-react'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Input,
} from '../components/ui'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog'
import { Label } from '../components/ui/label'
import { Select, SelectOption } from '../components/ui'
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, useResetPassword, type User } from '../hooks/useUsers'
import { useRoles } from '../hooks/useRoles'
import { useAuth } from '../providers/AuthProvider'

export function Users() {
  const { data: users, isLoading, error } = useUsers()
  const { data: rolesData } = useRoles()
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const deleteUser = useDeleteUser()
  const resetPassword = useResetPassword()
  const { user: currentUser } = useAuth()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [deletingUser, setDeletingUser] = useState<User | null>(null)
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null)

  // Form state
  const [formEmail, setFormEmail] = useState('')
  const [formPhone, setFormPhone] = useState('')
  const [formPassword, setFormPassword] = useState('')
  const [formRoleId, setFormRoleId] = useState('')
  const [formError, setFormError] = useState('')

  // Search
  const [searchQuery, setSearchQuery] = useState('')

  // Reset password form
  const [newPassword, setNewPassword] = useState('')
  const [resetError, setResetError] = useState('')
  const [resetSuccess, setResetSuccess] = useState('')

  const roles = rolesData?.data ?? []
  const userList = users ?? []

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return userList
    return userList.filter(user =>
      user.email.toLowerCase().includes(q) ||
      (user.phone && user.phone.toLowerCase().includes(q)) ||
      (user.role?.name && user.role.name.toLowerCase().includes(q)) ||
      (user.employee?.fullName && user.employee.fullName.toLowerCase().includes(q))
    )
  }, [userList, searchQuery])

  const openCreate = () => {
    setEditingUser(null)
    setFormEmail('')
    setFormPhone('')
    setFormPassword('')
    setFormRoleId('')
    setFormError('')
    setIsFormOpen(true)
  }

  const openEdit = (user: User) => {
    setEditingUser(user)
    setFormEmail(user.email)
    setFormPhone(user.phone || '')
    setFormPassword('')
    setFormRoleId(user.roleId || user.role?.id || '')
    setFormError('')
    setIsFormOpen(true)
  }

  const openDelete = (user: User) => {
    setDeletingUser(user)
    setFormError('')
    setIsDeleteOpen(true)
  }

  const openResetPassword = (user: User) => {
    setResetPasswordUser(user)
    setNewPassword('')
    setResetError('')
    setResetSuccess('')
    setIsResetPasswordOpen(true)
  }

  const handleSave = async () => {
    if (!formEmail.trim()) {
      setFormError('Введите email')
      return
    }
    if (!editingUser && !formPassword) {
      setFormError('Введите пароль')
      return
    }
    if (!editingUser && formPassword.length < 6) {
      setFormError('Пароль должен быть не менее 6 символов')
      return
    }
    if (!formRoleId) {
      setFormError('Выберите роль')
      return
    }
    setFormError('')

    try {
      if (editingUser) {
        await updateUser.mutateAsync({
          id: editingUser.id,
          data: {
            email: formEmail.trim(),
            phone: formPhone.trim() || null,
            roleId: formRoleId,
          },
        })
      } else {
        await createUser.mutateAsync({
          email: formEmail.trim(),
          password: formPassword,
          phone: formPhone.trim() || undefined,
          roleId: formRoleId,
        })
      }
      setIsFormOpen(false)
    } catch (err: any) {
      setFormError(err?.message || 'Ошибка сохранения')
    }
  }

  const handleDelete = async () => {
    if (!deletingUser) return
    try {
      await deleteUser.mutateAsync(deletingUser.id)
      setIsDeleteOpen(false)
      setDeletingUser(null)
    } catch (err: any) {
      setFormError(err?.message || 'Ошибка удаления')
    }
  }

  const handleToggleActive = async (user: User) => {
    try {
      await updateUser.mutateAsync({
        id: user.id,
        data: { isActive: !user.isActive },
      })
    } catch (err: any) {
      console.error('Failed to toggle user active status:', err)
    }
  }

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return
    if (!newPassword || newPassword.length < 6) {
      setResetError('Пароль должен быть не менее 6 символов')
      return
    }
    setResetError('')
    setResetSuccess('')

    try {
      await resetPassword.mutateAsync({
        userId: resetPasswordUser.id,
        newPassword,
      })
      setResetSuccess('Пароль успешно сброшен')
      setNewPassword('')
    } catch (err: any) {
      setResetError(err?.message || 'Ошибка сброса пароля')
    }
  }

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (isLoading) return <LoadingTable />
  if (error) return <ErrorState title="Ошибка загрузки пользователей" description={String(error)} />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserCog className="w-7 h-7" />
            Пользователи
          </h1>
          <p className="text-gray-500 mt-1">Управление пользователями админ-панели</p>
        </div>
        <Button onClick={openCreate} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Создать пользователя
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle>Пользователи ({filteredUsers.length})</CardTitle>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Поиск по email, телефону, роли..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto max-h-[calc(100vh-280px)]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Сотрудник</TableHead>
                <TableHead>Телефон</TableHead>
                <TableHead>Роль</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Последний вход</TableHead>
                <TableHead>Создан</TableHead>
                <TableHead className="w-[160px]">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map(user => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell className="text-sm">{user.employee?.fullName || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="text-gray-500 text-sm">{user.phone || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={user.role?.permissions?.all ? 'default' : 'secondary'}>
                      {user.role?.name || 'Без роли'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.isActive ? (
                      <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100">
                        Активен
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-red-100 text-red-800">
                        Заблокирован
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-gray-500 text-sm">{formatDate(user.lastLoginAt)}</TableCell>
                  <TableCell className="text-gray-500 text-sm">{formatDate(user.createdAt)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(user)}
                        title="Редактировать"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openResetPassword(user)}
                        title="Сбросить пароль"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      {user.id !== currentUser?.id && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(user)}
                            title={user.isActive ? 'Заблокировать' : 'Активировать'}
                          >
                            {user.isActive ? (
                              <Ban className="w-4 h-4 text-orange-500" />
                            ) : (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDelete(user)}
                            title="Удалить"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-400 py-8">
                    {searchQuery.trim() ? 'Ничего не найдено' : 'Нет пользователей'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Редактирование пользователя' : 'Создание пользователя'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="user-email">Email</Label>
              <Input
                id="user-email"
                type="email"
                value={formEmail}
                onChange={e => setFormEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-phone">Телефон</Label>
              <Input
                id="user-phone"
                value={formPhone}
                onChange={e => setFormPhone(e.target.value)}
                placeholder="+77001234567"
              />
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="user-password">Пароль</Label>
                <Input
                  id="user-password"
                  type="password"
                  value={formPassword}
                  onChange={e => setFormPassword(e.target.value)}
                  placeholder="Минимум 6 символов"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="user-role">Роль</Label>
              <Select
                id="user-role"
                value={formRoleId}
                onChange={e => setFormRoleId(e.target.value)}
              >
                <SelectOption value="">Выберите роль</SelectOption>
                {roles.map(role => (
                  <SelectOption key={role.id} value={role.id}>
                    {role.name}
                  </SelectOption>
                ))}
              </Select>
            </div>

            {formError && (
              <p className="text-sm text-red-600">{formError}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              disabled={createUser.isPending || updateUser.isPending}
            >
              {createUser.isPending || updateUser.isPending ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удаление пользователя</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-gray-600">
            Вы уверены, что хотите удалить пользователя <strong>{deletingUser?.email}</strong>?
            Это действие необратимо.
          </p>
          {formError && (
            <p className="text-sm text-red-600">{formError}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteUser.isPending}
            >
              {deleteUser.isPending ? 'Удаление...' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordOpen} onOpenChange={setIsResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Сброс пароля</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-600">
              Установить новый пароль для <strong>{resetPasswordUser?.email}</strong>
            </p>
            <div className="space-y-2">
              <Label htmlFor="new-password">Новый пароль</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Минимум 6 символов"
              />
            </div>
            {resetError && (
              <p className="text-sm text-red-600">{resetError}</p>
            )}
            {resetSuccess && (
              <p className="text-sm text-green-600">{resetSuccess}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResetPasswordOpen(false)}>
              Закрыть
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetPassword.isPending}
            >
              {resetPassword.isPending ? 'Сброс...' : 'Сбросить пароль'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
